import { NetworkStatus, useQuery } from "@apollo/client";
import { useMemo } from "react";
import { AutocompleteOptionType } from "../../../common/models/autocomplete";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { GET_STATISTICS_DATA } from "../../../common/operations/queries";
import {
  useSelectedDeploymentHealthLocation,
  useSelectedDeploymentHealthTagLocations,
} from "../variables/deploymentHealth";

interface FacilityRealtimeSummary {
  totalFacilities: number;
  expectedFacilities: number;
  onlineExpectedFacilities: number;
  degradedExpectedFacilities: number;
  excludedFacilities: number;
  customerRealtimeUptimePct: number | null;
}

interface FacilityRealtimeFacility {
  locationId: string | null;
  locationName: string | null;
  isExpectedOnline: boolean;
  isDegraded: boolean;
  isOnline: boolean;
  uptimePct: number | null;
}

interface FacilityRealtimeUptimeModel {
  event_type: string;
  customerId: string;
  generatedAtEpochMs: number;
  summary: FacilityRealtimeSummary;
  facilities: FacilityRealtimeFacility[];
}

const toLocationFilters = (
  locationValue: string | undefined,
  tagLocations: AutocompleteOptionType[] | null
): string[] | undefined => {
  if (locationValue) return [locationValue];

  if (!tagLocations?.length) return undefined;

  const values = tagLocations
    .map(item => item?.value)
    .filter(
      (item): item is string => typeof item === "string" && item.length > 0
    );

  return values.length ? values : undefined;
};

const parsePayload = (value: unknown): FacilityRealtimeUptimeModel | null => {
  if (!value) return null;

  try {
    const parsed =
      typeof value === "string"
        ? (JSON.parse(value) as FacilityRealtimeUptimeModel)
        : (value as FacilityRealtimeUptimeModel);

    if (!parsed || typeof parsed !== "object") return null;

    return parsed;
  } catch {
    return null;
  }
};

export const useFacilityRealtimeUptime = () => {
  const selectedCustomerId = useCustomerIdGuard();
  const locationVariable = useSelectedDeploymentHealthLocation();
  const tagLocationsVariable = useSelectedDeploymentHealthTagLocations();

  const locations = useMemo(
    () => toLocationFilters(locationVariable?.value, tagLocationsVariable),
    [locationVariable?.value, tagLocationsVariable]
  );

  const variables = useMemo(
    () =>
      selectedCustomerId
        ? {
            input: {
              customerId: selectedCustomerId,
              // backend supports this new enum; keep as literal to avoid frontend codegen coupling
              eventType: "facilityRealtimeUptime",
              ...(locations?.length ? { locations } : {}),
            },
          }
        : undefined,
    [selectedCustomerId, locations]
  );

  const { data, loading, networkStatus } = useQuery(GET_STATISTICS_DATA, {
    variables,
    fetchPolicy: "no-cache",
    notifyOnNetworkStatusChange: true,
    pollInterval: 60000,
    skip: !variables,
  });

  const parsed = useMemo(
    () => parsePayload(data?.getStatisticsData),
    [data?.getStatisticsData]
  );

  return {
    data: parsed,
    loading,
    refetching: networkStatus === NetworkStatus.refetch,
  };
};
